import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import UniversalLayout from "./components/UniversalLayout";
import { useAuthContext } from "./components/AuthProvider";
import { subscribe, sendCAPIEvent } from "./lib/api";
import { trackMetaEvent } from "./lib/meta";
import { getAdAttribution } from "./lib/adAttribution";
import "./PricingPage.css";

const BILLING_MARKUP = 0.1;

const VIDEO_MODEL_PRICES = [
  { key: "kling25", label: "Kling 2.5 Turbo (Standard I2V)", pricePerSecond: 0.042, category: "Kling family" },
  { key: "kling26", label: "Kling 2.6 Pro (I2V + Audio)", pricePerSecond: 0.14, category: "Kling family" },
  { key: "klingo1", label: "Kling O1 (Image to Video)", pricePerSecond: 0.112, category: "Kling family" },
  { key: "kling3", label: "Kling 3 Pro (I2V + Audio)", pricePerSecond: 0.336, category: "Kling family" },
  { key: "kling3standard", label: "Kling 3 Standard (I2V + Audio)", pricePerSecond: 0.252, category: "Kling family" },
  { key: "klingo3", label: "Kling O3 Standard (Reference to Video + Audio)", pricePerSecond: 0.224, category: "Kling family" },
  { key: "klingo3pro", label: "Kling O3 Pro (Reference to Video + Audio)", pricePerSecond: 0.336, category: "Kling family" },
  { key: "klingo3-standard-edit", label: "Kling O3 Edit Video Standard", pricePerSecond: 0.126, category: "Kling family" },
  { key: "klingo3-pro-edit", label: "Kling O3 Edit Video Pro", pricePerSecond: 0.168, category: "Kling family" },
  { key: "kling-o1-reference", label: "Kling O1 Reference Alias", pricePerSecond: 0.112, category: "Kling family" },
  { key: "kling25flf", label: "Kling 2.5 FLF Alias", pricePerSecond: 0.042, category: "Kling family" },
  { key: "kling26flf", label: "Kling 2.6 FLF Alias", pricePerSecond: 0.07, category: "Kling family" },
  { key: "kling26mc", label: "Kling 2.6 Motion Control", pricePerSecond: 0.07, category: "Kling family" },
  { key: "kling26mcpro", label: "Kling 2.6 Motion Control Pro", pricePerSecond: 0.112, category: "Kling family" },
  { key: "kling3mc", label: "Kling 3 Motion Control Standard", pricePerSecond: 0.126, category: "Kling family" },
  { key: "kling3mcpro", label: "Kling 3 Motion Control Pro", pricePerSecond: 0.168, category: "Kling family" },
  { key: "veo3.1", label: "Veo 3.1 Fast", pricePerSecond: 0.15, category: "Veo family" },
  { key: "veo31", label: "Veo 3.1 FLF Alias", pricePerSecond: 0.15, category: "Veo family" },
  { key: "gemini-omni-flash-text", label: "Gemini Omni Flash (Text to Video)", pricePerSecond: 0.125, category: "Gemini Omni Flash family" },
  { key: "gemini-omni-flash-i2v", label: "Gemini Omni Flash (Image to Video)", pricePerSecond: 0.13, category: "Gemini Omni Flash family" },
  { key: "gemini-omni-flash-reference", label: "Gemini Omni Flash (Reference to Video)", pricePerSecond: 0.13, category: "Gemini Omni Flash family" },
  { key: "gemini-omni-flash-edit", label: "Gemini Omni Flash (Video to Video)", pricePerSecond: 0.13, category: "Gemini Omni Flash family" },
  { key: "seedance", label: "Seedance v1 Pro Fast", pricePerSecond: 0.0486, category: "Seedance family" },
  { key: "seedance15", label: "Seedance v1.5 Pro", pricePerSecond: 0.052, category: "Seedance family" },
  { key: "seedance_2.0_fast:480p", modelKey: "seedance_2.0_fast", label: "Seedance 2.0 Fast · 480p", pricePerSecond: 0.061871, isFinalRate: true, category: "Seedance family" },
  { key: "seedance_2.0_fast:720p", modelKey: "seedance_2.0_fast", label: "Seedance 2.0 Fast · 720p", pricePerSecond: 0.133056, isFinalRate: true, category: "Seedance family" },
  { key: "seedance_2.0:480p", modelKey: "seedance_2.0", label: "Seedance 2.0 Standard · 480p", pricePerSecond: 0.077339, isFinalRate: true, category: "Seedance family" },
  { key: "seedance_2.0:720p", modelKey: "seedance_2.0", label: "Seedance 2.0 Standard · 720p", pricePerSecond: 0.16632, isFinalRate: true, category: "Seedance family" },
  { key: "seedance_2.0:1080p", modelKey: "seedance_2.0", label: "Seedance 2.0 Standard · 1080p", pricePerSecond: 0.411642, isFinalRate: true, category: "Seedance family" },
  { key: "ltxv2", label: "LTX v2 Fast (I2V)", pricePerSecond: 0.04, category: "LTX family" },
  { key: "ltx2_v2v", label: "LTX v2 (V2V)", pricePerSecond: 0.04147, category: "LTX family" },
  { key: "ltx2_v2v_distilled", label: "LTX v2 Distilled (V2V)", pricePerSecond: 0.01843, category: "LTX family" },
  { key: "sora2", label: "Sora 2 (Image to Video)", pricePerSecond: 0.1, category: "Sora family" },
  { key: "sora2_remix", label: "Sora 2 Remix (Video to Video)", pricePerSecond: 0.1, category: "Sora family" },
  { key: "wan26", label: "Wan 2.6 (I2V 1080p)", pricePerSecond: 0.15, category: "Wan family" },
  { key: "wan26flf", label: "Wan 2.6 FLF (480p)", pricePerSecond: 0.05, category: "Wan family" },
  { key: "wanflf14b", label: "Wan 2.1 14B FLF (Legacy)", pricePerSecond: 0.08, category: "Wan family" },
  { key: "wan21", label: "Wan 2.1 (Legacy T2V)", pricePerSecond: 0.08, category: "Wan family" },
  { key: "lumaray2", label: "Luma Ray 2 (I2V)", pricePerSecond: 0.4, category: "Other models" },
  { key: "minimax", label: "MiniMax Video", pricePerSecond: 0.08333, category: "Other models" },
  { key: "hunyuan", label: "Hunyuan Video", pricePerSecond: 0.08, category: "Other models" },
  { key: "cogvideox", label: "CogVideoX 5B", pricePerSecond: 0.03333, category: "Other models" },
  { key: "mochi", label: "Mochi v1", pricePerSecond: 0.08, category: "Other models" },
  { key: "lucy_edit_fast", label: "Lucy Edit Fast", pricePerSecond: 0.04, category: "Other models" },
  { key: "lucy_edit_pro", label: "Lucy Edit Pro (720p)", pricePerSecond: 0.15, category: "Other models" },
  { key: "lucy_restyle", label: "Lucy Restyle", pricePerSecond: 0.01, category: "Other models" },
];

const IMAGE_MODEL_PRICES = [
  { key: "nano-banana", label: "Nano Banana", pricePerImage: 0.039 },
  { key: "nano-banana-pro", label: "Nano Banana Pro", pricePerImage: 0.15 },
  { key: "nano-banana-2", label: "Nano Banana 2", pricePerImage: 0.08 },
  { key: "nano-banana-lite", label: "Nano Banana Lite", pricePerImage: 0.02 },
  { key: "gpt-image-1", label: "GPT-Image 1.5", pricePerImage: 0.034 },
  { key: "gpt-image-1-mini", label: "GPT-Image 1 Mini", pricePerImage: 0.011 },
  { key: "flux-schnell", label: "Flux Schnell", pricePerImage: 0.003 },
  { key: "flux-2", label: "Flux 2", pricePerImage: 0.012 },
  { key: "z-image", label: "Z-Image Turbo", pricePerImage: 0.005 },
  { key: "qwen-image-edit-plus", label: "Qwen Image Edit (Camera Angles)", pricePerImage: 0.035 },
  { key: "qwen-image-3", label: "Qwen Image 3 (1K)", pricePerImage: 0.04 },
  { key: "grok-imagine-image-2", label: "Grok Imagine Image 2.0 (Medium · 1K)", pricePerImage: 0.06 },
];

function formatDollars(value) {
  return `$${value.toFixed(2)}`;
}

function formatCents(value) {
  if (value < 0.01) return "<$0.01";
  return `$${value.toFixed(3)}`;
}

function formatVideoRate(value) {
  return value < 0.1 ? formatCents(value) : formatDollars(value);
}

function sortRows(rows, sortKey, direction) {
  const sorted = [...rows].sort((a, b) => {
    const aValue = a[sortKey];
    const bValue = b[sortKey];
    if (typeof aValue === "number" && typeof bValue === "number") return aValue - bValue;
    return String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: "base" });
  });
  return direction === "asc" ? sorted : sorted.reverse();
}

function PricingPage() {
  const { user, openAuthModal } = useAuthContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "finalPerSecond", direction: "asc" });
  const [subscribing, setSubscribing] = useState(null);

  // Meta Pixel + CAPI: ViewContent for pricing page
  useEffect(() => {
    const eventId = trackMetaEvent('ViewContent', { content_type: 'pricing' });
    sendCAPIEvent('ViewContent', eventId, { content_type: 'pricing' }, getAdAttribution()).catch(() => {});
  }, []);

  const pricedRows = VIDEO_MODEL_PRICES.map((model) => ({
    ...model,
    finalPerSecond: model.isFinalRate
      ? model.pricePerSecond
      : model.pricePerSecond * (1 + BILLING_MARKUP),
  })).sort((a, b) => a.finalPerSecond - b.finalPerSecond);

  const imagePricedRows = IMAGE_MODEL_PRICES.map((model) => ({
    ...model,
    finalPrice: model.pricePerImage * (1 + BILLING_MARKUP),
  }));

  const displayedRows = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filteredRows = normalizedQuery
      ? pricedRows.filter(
          (row) =>
            row.label.toLowerCase().includes(normalizedQuery) ||
            row.category.toLowerCase().includes(normalizedQuery) ||
            row.key.toLowerCase().includes(normalizedQuery) ||
            (row.modelKey || "").toLowerCase().includes(normalizedQuery)
        )
      : pricedRows;
    return sortRows(filteredRows, sortConfig.key, sortConfig.direction);
  }, [pricedRows, searchQuery, sortConfig.direction, sortConfig.key]);

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      return { key, direction: "asc" };
    });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return "\u21C5";
    return sortConfig.direction === "asc" ? "\u2191" : "\u2193";
  };

  async function startCheckout(tier) {
    setSubscribing(tier);
    try {
      const tierPrice = tier === 'studio' ? 200 : 20;
      const attribution = getAdAttribution();
      const eventId = trackMetaEvent('InitiateCheckout', {
        value: tierPrice,
        currency: 'USD',
        content_type: 'subscription',
        num_items: 1,
      });
      sendCAPIEvent('InitiateCheckout', eventId, {
        value: tierPrice,
        currency: 'USD',
        num_items: 1,
      }, attribution).catch(() => {});
      const successUrl = `${window.location.origin}/account?subscription=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/pricing`;
      const result = await subscribe(tier, successUrl, cancelUrl, { eventId, fbc: attribution.fbc, fbp: attribution.fbp });
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      alert("Failed to start checkout");
    } finally {
      setSubscribing(null);
    }
  }

  const handleSubscribe = (tier) => {
    if (!user) {
      localStorage.setItem('latted_pending_subscribe_tier', tier);
      openAuthModal();
      return;
    }
    startCheckout(tier);
  };

  return (
    <UniversalLayout>
      <div className="pricing-page">
        <section className="pricing-hero">
          <div className="pricing-hero__badge">Choose Your Plan</div>
          <h1 className="pricing-hero__title">
            Simple pricing, <span>powerful creation</span>
          </h1>
          <p className="pricing-hero__subtitle">
            Start free with pay-as-you-go, or subscribe for included video allowance and unlimited images.
          </p>
        </section>

        {/* Plan Cards */}
        <section className="pricing-plans" aria-label="Subscription plans">
          {/* Pay As You Go */}
          <article className="pricing-plan-card">
            <div className="pricing-plan-card__header">
              <h3>Pay As You Go</h3>
              <div className="pricing-plan-card__price">
                <span className="pricing-plan-card__amount">$0</span>
                <span className="pricing-plan-card__period">/month</span>
              </div>
            </div>
            <ul className="pricing-plan-card__features">
              <li>Per-model video pricing (see table below)</li>
              <li>Per-model image pricing (see table below)</li>
              <li>No monthly commitment</li>
              <li>Buy credits or use metered billing</li>
            </ul>
            <Link to="/" className="pricing-plan-card__button pricing-plan-card__button--secondary">
              Get Started Free
            </Link>
          </article>

          {/* Creator */}
          <article className="pricing-plan-card pricing-plan-card--featured">
            <div className="pricing-plan-card__badge">Most Popular</div>
            <div className="pricing-plan-card__header">
              <h3>Creator</h3>
              <div className="pricing-plan-card__price">
                <span className="pricing-plan-card__amount">$20</span>
                <span className="pricing-plan-card__period">/month</span>
              </div>
            </div>
            <ul className="pricing-plan-card__features">
              <li><strong>$30/mo video generation allowance</strong></li>
              <li><strong>Unlimited image generation</strong></li>
              <li><strong>Unlimited voice generation</strong></li>
              <li>Pay-as-you-go after allowance used</li>
              <li>Priority processing</li>
            </ul>
            <button
              className="pricing-plan-card__button pricing-plan-card__button--primary"
              onClick={() => handleSubscribe("creator")}
              disabled={subscribing === "creator"}
            >
              {subscribing === "creator" ? "Loading..." : "Subscribe to Creator"}
            </button>
          </article>

          {/* Studio */}
          <article className="pricing-plan-card">
            <div className="pricing-plan-card__header">
              <h3>Studio</h3>
              <div className="pricing-plan-card__price">
                <span className="pricing-plan-card__amount">$200</span>
                <span className="pricing-plan-card__period">/month</span>
              </div>
            </div>
            <ul className="pricing-plan-card__features">
              <li><strong>$500/mo video generation allowance</strong></li>
              <li><strong>Unlimited image generation</strong></li>
              <li><strong>Unlimited voice generation</strong></li>
              <li>Pay-as-you-go after allowance used</li>
              <li>Priority processing</li>
              <li>Best value for high-volume creators</li>
            </ul>
            <button
              className="pricing-plan-card__button pricing-plan-card__button--primary"
              onClick={() => handleSubscribe("studio")}
              disabled={subscribing === "studio"}
            >
              {subscribing === "studio" ? "Loading..." : "Subscribe to Studio"}
            </button>
          </article>
        </section>

        {/* Image Pricing Table (for PAYG users) */}
        <section className="pricing-table-section" aria-label="Image model pricing">
          <div className="pricing-table-section__header">
            <div className="pricing-table-section__heading">
              <div>
                <h2>Image model pricing</h2>
                <p>Pay-as-you-go rates for non-subscribers. Subscribers get unlimited images.</p>
              </div>
            </div>
          </div>
          <div className="pricing-table-wrapper">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Price / image</th>
                </tr>
              </thead>
              <tbody>
                {imagePricedRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td>{formatCents(row.finalPrice)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Video Pricing Table */}
        <section className="pricing-table-section" aria-label="Video model pricing table">
          <div className="pricing-table-section__header">
            <div className="pricing-table-section__heading">
              <div>
                <h2>Detailed video model pricing</h2>
                <p>All prices shown are final pay-as-you-go rates. Seedance 2 output varies by resolution; video references are billed separately by input duration.</p>
              </div>
              <div className="pricing-table-search">
                <span className="pricing-table-search__icon" aria-hidden="true">{"\u2315"}</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by model name..."
                  aria-label="Search pricing models"
                />
              </div>
            </div>
          </div>
          <div className="pricing-table-wrapper">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>
                    <button type="button" className="pricing-sort-button" onClick={() => handleSort("label")}>
                      Model <span>{renderSortArrow("label")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="pricing-sort-button" onClick={() => handleSort("category")}>
                      Family <span>{renderSortArrow("category")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="pricing-sort-button" onClick={() => handleSort("finalPerSecond")}>
                      Output / sec <span>{renderSortArrow("finalPerSecond")}</span>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="pricing-sort-button" onClick={() => handleSort("key")}>
                      Model key <span>{renderSortArrow("key")}</span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td>{row.category}</td>
                    <td>{formatVideoRate(row.finalPerSecond)}</td>
                    <td><code>{row.modelKey || row.key}</code></td>
                  </tr>
                ))}
                {displayedRows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="pricing-table__empty">No models match your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="pricing-table-note">
            <strong>Seedance 2 reference videos:</strong> reference input video duration is charged at 0.6× the regular output price for the selected tier and resolution. Image and audio references do not add a duration charge.
          </div>
        </section>

        <section className="pricing-cta">
          <h2>Ready to generate?</h2>
          <p>
            Start with pay-as-you-go or subscribe for the best value.
            Use any of 40+ video models and 10+ image models.
          </p>
          <Link to="/" className="pricing-cta__button">
            Start creating on Latted
          </Link>
        </section>
      </div>
    </UniversalLayout>
  );
}

export default PricingPage;
